import type { ReactElement } from "react";
import type { LucideIcon } from "lucide-react";
import { Clock, Eye, FolderOpen, Heart, ListPlus, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { TableCell, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle } from "../types";
import { formatDuration, formatNumber, formatPercentage, getCompletionColor, getViewsColor } from "../utils/formatters";
import { TitleActionsDropdown } from "./TitleActionsDropdown";

interface QuickStatsRowProps {
  readonly title: BulkEditTitle;
  readonly isSelected: boolean;
  readonly onSelect: (titleId: string, selected: boolean) => void;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
  readonly onManageCollections: (titleId: string, titleName: string) => void;
}

/**
 * Metric cell component to reduce duplication
 */
function MetricCell({
  icon: Icon,
  value,
  className = "text-neutral-300",
}: Readonly<{
  icon: LucideIcon;
  value: string;
  className?: string;
}>): ReactElement {
  return (
    <TableCell className="text-center">
      <div className="flex items-center justify-center gap-2">
        <Icon className="w-4 h-4 text-neutral-500" />
        <span className={className}>{value}</span>
      </div>
    </TableCell>
  );
}

/**
 * Quick Stats Row Component
 * Displays title analytics metrics in the bulk editor
 */
export function QuickStatsRow({
  title,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
  onManageCollections,
}: Readonly<QuickStatsRowProps>): ReactElement {
  const stats = title.stats;

  // Default values if stats are not available
  const views = stats?.views ?? 0;
  const watchTime = stats?.watchTimeSeconds ?? 0;
  const completion = stats?.completionRate ?? 0;
  const likes = stats?.likes ?? 0;
  const watchlistAdds = stats?.watchlistAdds ?? 0;
  const collectionCount = stats?.collectionCount ?? 0;

  return (
    <TableRow className="hover:bg-neutral-900/30">
      {/* Checkbox */}
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect(title.id, checked === true)}
          aria-label={`Select ${title.name}`}
        />
      </TableCell>

      {/* Title */}
      <TableCell className="font-medium">
        <Link to={`/admin/titles/${title.id}/edit`} className="text-white hover:text-lumi-accent transition-colors">
          {title.name}
        </Link>
      </TableCell>

      {/* Metrics */}
      <MetricCell icon={Eye} value={formatNumber(views)} className={getViewsColor(views)} />
      <MetricCell icon={Clock} value={formatDuration(watchTime)} />
      <MetricCell icon={TrendingUp} value={formatPercentage(completion)} className={getCompletionColor(completion)} />
      <MetricCell icon={Heart} value={formatNumber(likes)} />
      <MetricCell icon={ListPlus} value={formatNumber(watchlistAdds)} />

      {/* Collections */}
      <TableCell className="text-center">
        <button
          type="button"
          onClick={() => onManageCollections(title.id, title.name)}
          className="inline-flex items-center justify-center gap-2 px-2 py-1 rounded hover:bg-neutral-800 transition-colors group"
          title="Manage collections"
        >
          <FolderOpen className="w-4 h-4 text-neutral-500 group-hover:text-lumi-accent" />
          <span className="text-neutral-300 group-hover:text-white">{collectionCount}</span>
        </button>
      </TableCell>

      {/* Status */}
      <TableCell>
        <span
          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
            title.published
              ? "bg-green-900/20 text-green-400 border border-green-800"
              : "bg-neutral-800 text-neutral-400 border border-neutral-700"
          }`}
        >
          {title.published ? "Published" : "Draft"}
        </span>
      </TableCell>

      {/* Actions */}
      <TableCell className="text-center">
        <TitleActionsDropdown titleId={title.id} titleName={title.name} onDelete={onDelete} onDuplicate={onDuplicate} />
      </TableCell>
    </TableRow>
  );
}
